import { defineMessages } from "../define";

/**
 * Monitoring board, "Unevaluated" tab (monitoring.unevaluated.*): captured pizzas that never got a verdict,
 * per day, with the reason and a way to evaluate a day again.
 */
export default defineMessages({
  en: {
    "monitoring.tab.unevaluated": "Unevaluated",

    "monitoring.unevaluated.title": "Unevaluated photos",
    "monitoring.unevaluated.description":
      "A capture is evaluated once, right after its photo arrives. When that run fails nothing retries it, so the pizza stays without a verdict and is left out of the pizza list and the dashboard. Days are local ({timezone}); the reason comes from the pizza's last error, which is kept {days} days.",
    "monitoring.unevaluated.period": "Period",
    "monitoring.unevaluated.period.days": "{count} d",
    "monitoring.unevaluated.period.long": "last {count} days",
    "monitoring.unevaluated.total": "Without a verdict",
    "monitoring.unevaluated.totalHint": "Captured pizzas of the branches without an evaluation",
    "monitoring.unevaluated.evaluable": "Can be evaluated",
    "monitoring.unevaluated.evaluableHint": "The photo is in S3 and no evaluation can still be running",
    "monitoring.unevaluated.noPhoto": "Without a photo",
    "monitoring.unevaluated.noPhotoHint": "Nothing to evaluate: the photo never reached S3",
    "monitoring.unevaluated.empty": "Every captured pizza of the period has a verdict.",
    "monitoring.unevaluated.truncated": "Only the newest {count} pizzas are listed.",
    "monitoring.unevaluated.pizzas.one": "{count} pizza",
    "monitoring.unevaluated.pizzas.few": "{count} pizzas",
    "monitoring.unevaluated.pizzas.other": "{count} pizzas",
    "monitoring.unevaluated.dayEvaluable": "{count} can be evaluated",
    "monitoring.unevaluated.showPizzas": "Show pizzas",
    "monitoring.unevaluated.hidePizzas": "Hide pizzas",
    "monitoring.unevaluated.evaluateDay": "Evaluate again ({count})",
    "monitoring.unevaluated.evaluateDayHint":
      "Runs the evaluation of the day's pizzas whose photo is in S3, {parallel} at a time, like opening each pizza in the admin. Keep the page open until it finishes.",
    "monitoring.unevaluated.evaluateOne": "Evaluate",
    "monitoring.unevaluated.stop": "Stop",
    "monitoring.unevaluated.busy": "Another evaluation is running",
    "monitoring.unevaluated.progress": "{done} / {total} evaluated",
    "monitoring.unevaluated.progressFailed.one": "{count} failed",
    "monitoring.unevaluated.progressFailed.few": "{count} failed",
    "monitoring.unevaluated.progressFailed.other": "{count} failed",
    "monitoring.unevaluated.finished": "Finished: {ok} evaluated, {failed} failed",
    "monitoring.unevaluated.stopped": "Stopped: {ok} evaluated, {failed} failed",
    "monitoring.unevaluated.result.running": "Evaluating…",
    "monitoring.unevaluated.result.ok": "Evaluated",
    "monitoring.unevaluated.result.failed": "Failed",
    "monitoring.unevaluated.capturedAt": "captured",
    "monitoring.unevaluated.photo.yes": "Photo in S3",
    "monitoring.unevaluated.photo.no": "No photo",
    "monitoring.unevaluated.photo.unknown": "Photo not checked",
    "monitoring.unevaluated.photo.unknownHint": "S3 could not be asked; try again later",
    "monitoring.unevaluated.lastError": "Last error",

    "monitoring.unevaluated.reason.pending": "Evaluating",
    "monitoring.unevaluated.reason.pendingHint": "Captured moments ago: the evaluation may still be running.",
    "monitoring.unevaluated.reason.no_photo": "Photo never arrived",
    "monitoring.unevaluated.reason.no_photoHint":
      "The camera took the trigger, but its upload never reached the backend (camera or network). Nothing to evaluate.",
    "monitoring.unevaluated.reason.photo_lost": "Photo not stored",
    "monitoring.unevaluated.reason.photo_lostHint":
      "The backend received the upload but did not store the photo (the upload was refused or the S3 write failed). Nothing to evaluate.",
    "monitoring.unevaluated.reason.no_pod": "No GPU pod",
    "monitoring.unevaluated.reason.no_podHint":
      "There was no GPU pod in the pool (the autoscaler scaled to zero), so the evaluation failed at once.",
    "monitoring.unevaluated.reason.pod_failed": "GPU pod did not answer",
    "monitoring.unevaluated.reason.pod_failedHint": "Every attempt at the GPU pod failed (timeouts or connection errors).",
    "monitoring.unevaluated.reason.evaluation_error": "Evaluation error",
    "monitoring.unevaluated.reason.evaluation_errorHint": "The evaluation failed for another reason, see the error.",
    "monitoring.unevaluated.reason.no_record": "No error recorded",
    "monitoring.unevaluated.reason.no_recordHint":
      "The photo is in S3 but no error was recorded for the pizza, e.g. the backend restarted during the evaluation.",
    "monitoring.unevaluated.reason.no_record_old": "Older than the error log",
    "monitoring.unevaluated.reason.no_record_oldHint":
      "Older than the stored error log (errors are kept {days} days), so the reason is no longer known.",
  },
  cs: {
    "monitoring.tab.unevaluated": "Nevyhodnocené",

    "monitoring.unevaluated.title": "Nevyhodnocené snímky",
    "monitoring.unevaluated.description":
      "Snímek se vyhodnocuje jen jednou, hned po příchodu fotky. Když to selže, nic to znovu nezkusí, takže pizza zůstane bez výsledku a chybí v seznamu pizz i na dashboardu. Dny jsou podle místního času ({timezone}); důvod je poslední chyba pizzy, chyby se drží {days} dní.",
    "monitoring.unevaluated.period": "Období",
    "monitoring.unevaluated.period.days": "{count} d",
    "monitoring.unevaluated.period.long": "posledních {count} dní",
    "monitoring.unevaluated.total": "Bez výsledku",
    "monitoring.unevaluated.totalHint": "Pořízené pizzy poboček bez vyhodnocení",
    "monitoring.unevaluated.evaluable": "Lze vyhodnotit",
    "monitoring.unevaluated.evaluableHint": "Fotka je v S3 a žádné vyhodnocení už nemůže běžet",
    "monitoring.unevaluated.noPhoto": "Bez fotky",
    "monitoring.unevaluated.noPhotoHint": "Není co vyhodnotit: fotka se do S3 nedostala",
    "monitoring.unevaluated.empty": "Všechny pořízené pizzy v období mají výsledek.",
    "monitoring.unevaluated.truncated": "Zobrazeno jen nejnovějších {count} pizz.",
    "monitoring.unevaluated.pizzas.one": "{count} pizza",
    "monitoring.unevaluated.pizzas.few": "{count} pizzy",
    "monitoring.unevaluated.pizzas.other": "{count} pizz",
    "monitoring.unevaluated.dayEvaluable": "lze vyhodnotit {count}",
    "monitoring.unevaluated.showPizzas": "Zobrazit pizzy",
    "monitoring.unevaluated.hidePizzas": "Skrýt pizzy",
    "monitoring.unevaluated.evaluateDay": "Vyhodnotit znovu ({count})",
    "monitoring.unevaluated.evaluateDayHint":
      "Spustí vyhodnocení pizz daného dne, které mají fotku v S3, po {parallel} najednou, stejně jako otevření pizzy v adminu. Nech stránku otevřenou, dokud to nedoběhne.",
    "monitoring.unevaluated.evaluateOne": "Vyhodnotit",
    "monitoring.unevaluated.stop": "Zastavit",
    "monitoring.unevaluated.busy": "Běží jiné vyhodnocení",
    "monitoring.unevaluated.progress": "vyhodnoceno {done} / {total}",
    "monitoring.unevaluated.progressFailed.one": "{count} selhalo",
    "monitoring.unevaluated.progressFailed.few": "{count} selhaly",
    "monitoring.unevaluated.progressFailed.other": "{count} selhalo",
    "monitoring.unevaluated.finished": "Hotovo: vyhodnoceno {ok}, selhalo {failed}",
    "monitoring.unevaluated.stopped": "Zastaveno: vyhodnoceno {ok}, selhalo {failed}",
    "monitoring.unevaluated.result.running": "Vyhodnocuje se…",
    "monitoring.unevaluated.result.ok": "Vyhodnoceno",
    "monitoring.unevaluated.result.failed": "Selhalo",
    "monitoring.unevaluated.capturedAt": "pořízeno",
    "monitoring.unevaluated.photo.yes": "Fotka v S3",
    "monitoring.unevaluated.photo.no": "Bez fotky",
    "monitoring.unevaluated.photo.unknown": "Fotka neověřena",
    "monitoring.unevaluated.photo.unknownHint": "S3 se nepodařilo zeptat, zkus to později",
    "monitoring.unevaluated.lastError": "Poslední chyba",

    "monitoring.unevaluated.reason.pending": "Vyhodnocuje se",
    "monitoring.unevaluated.reason.pendingHint": "Pořízeno před chvílí, vyhodnocení možná ještě běží.",
    "monitoring.unevaluated.reason.no_photo": "Fotka nedorazila",
    "monitoring.unevaluated.reason.no_photoHint":
      "Kamera přijala spouštěč, ale její upload do backendu nikdy nedorazil (kamera nebo síť). Není co vyhodnotit.",
    "monitoring.unevaluated.reason.photo_lost": "Fotka se neuložila",
    "monitoring.unevaluated.reason.photo_lostHint":
      "Backend upload přijal, ale fotku neuložil (upload odmítl nebo selhal zápis do S3). Není co vyhodnotit.",
    "monitoring.unevaluated.reason.no_pod": "Žádný GPU pod",
    "monitoring.unevaluated.reason.no_podHint":
      "V poolu nebyl žádný GPU pod (autoscaler vypnul pody), vyhodnocení spadlo okamžitě.",
    "monitoring.unevaluated.reason.pod_failed": "GPU pod neodpověděl",
    "monitoring.unevaluated.reason.pod_failedHint": "Všechny pokusy o GPU pod selhaly (timeouty nebo chyby spojení).",
    "monitoring.unevaluated.reason.evaluation_error": "Chyba vyhodnocení",
    "monitoring.unevaluated.reason.evaluation_errorHint": "Vyhodnocení spadlo z jiného důvodu, viz chyba.",
    "monitoring.unevaluated.reason.no_record": "Bez záznamu chyby",
    "monitoring.unevaluated.reason.no_recordHint":
      "Fotka je v S3, ale k pizze se nezapsala žádná chyba, např. backend se během vyhodnocení restartoval.",
    "monitoring.unevaluated.reason.no_record_old": "Starší než záznamy chyb",
    "monitoring.unevaluated.reason.no_record_oldHint":
      "Starší než uložený log chyb (chyby se drží {days} dní), důvod už nejde zjistit.",
  },
});
